// src/components/ActivityList.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useActivities } from "../hooks/useActivities";
import ActivityFilters from "../components/crm/ActivityFilters";

function useDebouncedValue(value, delay = 400) {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

function formatYMD(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function addDays(base, days) {
  const d = new Date(base);
  d.setDate(d.getDate() + days);
  return d;
}

export default function ActivityList({
  mode = "global",
  cardId,
  pageSize = 30,
  initialQuery = {},
}) {
  const [page, setPage] = useState(1);

  // filtros centralizados no parent (ActivityFilters notifica via onChange)
  const [filters, setFilters] = useState({
    q: initialQuery.q || "",
    actions: Array.isArray(initialQuery.actions)
      ? initialQuery.actions
      : (initialQuery.actions || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
    actorUserId: initialQuery.actorUserId || "",
    targetType: initialQuery.targetType || "",
    targetId: initialQuery.targetId || "",
    dateFrom: initialQuery.dateFrom || "",
    dateTo: initialQuery.dateTo || "",
  });

  function handleFiltersChange(next) {
    setFilters(next);
    setPage(1);
  }

  // monta params para o hook (backend espera CSV em actions)
  const queryParams = useMemo(
    () => ({
      mode,
      cardId,
      page,
      itemsPerPage: pageSize,
      q: filters.q,
      actions: filters.actions.join(","),
      actorUserId: filters.actorUserId,
      targetType: filters.targetType,
      targetId: filters.targetId,
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
    }),
    [mode, cardId, page, pageSize, filters]
  );

  const { data, isLoading, isError, error, isFetching } =
    useActivities(queryParams);

  const items = Array.isArray(data?.data) ? data.data : [];
  const totalPages = Number(data?.totalPages || 1);
  const totalCount = Number(data?.totalCount || 0);

  const canPrev = page > 1;
  const canNext = page < totalPages;
  const goPrev = () => canPrev && setPage((p) => p - 1);
  const goNext = () => canNext && setPage((p) => p + 1);

  return (
    <div className="w-full mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold">
            {mode === "card" ? "Timeline do Card" : "Feed de Atividades"}
          </h2>
          <p className="text-sm text-gray-500">
            {mode === "card" ? (
              <>
                Listando atividades para o card{" "}
                <strong>#{String(cardId)}</strong>.
              </>
            ) : (
              <>Todas as atividades da empresa (com filtros).</>
            )}
          </p>
        </div>
        <div className="text-sm text-gray-600">
          <span className="inline-block bg-gray-100 px-3 py-1 rounded-full">
            {isFetching ? "Atualizando…" : `${totalCount} registros`}
          </span>
        </div>
      </div>

      <ActivityFilters value={filters} onChange={handleFiltersChange} />

      {/* Content */}
      <div className="bg-white border rounded-xl shadow-sm">
        {isLoading && (
          <div className="p-8 text-center text-gray-500">Carregando…</div>
        )}
        {isError && (
          <div className="p-4 text-red-600 border-b">
            {error?.message || "Erro ao carregar atividades"}
          </div>
        )}
        {!isLoading && !isError && items.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            Nenhuma atividade encontrada.
          </div>
        )}

        <ul className="divide-y">
          {items.map((it) => (
            <li key={it.id} className="p-4 flex gap-4">
              {/* badge simples pela ação */}
              <div className="flex-shrink-0">
                <span className="inline-flex select-none items-center justify-center w-10 h-10 rounded-full bg-gray-100 text-gray-700 text-sm font-semibold">
                  {actionEmoji(it.action)}
                </span>
              </div>

              <div className="flex-1 min-w-0">
                {/* <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">
                    {it.action || "Ação"}
                  </span>
                  {it.targetType && (
                    <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700">
                      {it.targetType}
                      {it.targetId ? `#${it.targetId}` : ""}
                    </span>
                  )}
                  {it.card && (
                    <span className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700">
                      Card #{it.card.id}: {it.card.title}
                    </span>
                  )}
                </div> */}
                {it.description && (
                  <p className="mt-1 text-sm text-gray-700">{it.description}</p>
                )}

                <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-500">
                  <span>{formatDateTime(it.createdAt)}</span>
                  {it.actor && (
                    <span>
                      • por{" "}
                      <strong className="text-gray-700">{it.actor.name}</strong>
                      {it.actor.email ? ` (${it.actor.email})` : ""}
                    </span>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>

        {/* Pagination */}
        <div className="flex items-center justify-between p-3 border-t bg-gray-50">
          <div className="text-sm text-gray-600">
            Página <strong>{page}</strong> de <strong>{totalPages}</strong>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-1.5 rounded border bg-white disabled:opacity-50"
              onClick={goPrev}
              disabled={!canPrev}
            >
              ← Anterior
            </button>
            <button
              className="px-3 py-1.5 rounded border bg-white disabled:opacity-50"
              onClick={goNext}
              disabled={!canNext}
            >
              Próxima →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatDateTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return String(iso);
  }
}

function actionEmoji(action = "") {
  const a = String(action).toUpperCase();
  if (a.includes("CREATED")) return "🆕";
  if (a.includes("UPDATED")) return "✏️";
  if (a.includes("DELETED")) return "🗑️";
  if (a.includes("DONE") || a.includes("COMPLETED")) return "✅";
  if (a.includes("CANCELED") || a.includes("CANCELLED")) return "🚫";
  if (a.includes("COMMENT")) return "💬";
  if (a.includes("LINKED")) return "🔗";
  if (a.includes("STATUS")) return "🏷️";
  if (a.includes("TASK")) return "📝";
  if (a.includes("FUNNEL")) return "🕳️";
  if (a.includes("STAGE")) return "📍";
  if (a.includes("LEAD")) return "👤";
  if (a.includes("CARD")) return "💳";
  return "📌";
}

function KVBlock({ title, data }) {
  const entries = React.useMemo(() => safeEntries(data), [data]);
  return (
    <div className="border rounded-lg bg-gray-50 p-3 overflow-auto">
      <div className="text-xs font-medium text-gray-600 mb-1">{title}</div>
      <table className="w-full text-xs">
        <tbody>
          {entries.map(([k, v]) => (
            <tr key={k} className="align-top">
              <td className="py-1 pr-2 text-gray-500 whitespace-nowrap">{k}</td>
              <td className="py-1 break-all">{renderValue(v)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function safeEntries(obj) {
  if (!obj || typeof obj !== "object") return [];
  try {
    return Object.entries(obj).slice(0, 12);
  } catch {
    return [];
  }
}

function renderValue(v) {
  if (v == null) return <span className="text-gray-400">null</span>;
  if (typeof v === "object") {
    try {
      return <code className="text-[11px]">{JSON.stringify(v)}</code>;
    } catch {
      return <code className="text-[11px]">{"{obj}"}</code>;
    }
  }
  return String(v);
}
